import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { invitesApi } from '../api';
import client from '../api/client';
import { useDesign } from '../contexts/DesignContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import LanguagePickerLarge from '../components/LanguagePickerLarge';
import { toast } from 'sonner';
import {
  Dumbbell, Mail, Lock, User, Loader2, UserCheck,
  Heart, Zap, Trophy, Flame, Shield, Target, Activity, Award, Crown,
  Eye, EyeOff, Check, X, BookmarkPlus
} from 'lucide-react';
import { useTranslation } from 'react-i18next';



const LOGO_ICONS = { Dumbbell, Heart, Zap, Trophy, Flame, Shield, Target, Activity, Award, Crown };

// Password strength calculator
function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;

  if (score <= 1) return { score: 1, labelKey: 'register.strengthWeak', color: '#ef4444' };
  if (score <= 2) return { score: 2, labelKey: 'register.strengthFair', color: '#f97316' };
  if (score <= 3) return { score: 3, labelKey: 'register.strengthGood', color: '#eab308' };
  if (score <= 4) return { score: 4, labelKey: 'register.strengthStrong', color: '#22c55e' };
  return { score: 5, labelKey: 'register.strengthVeryStrong', color: '#10b981' };
}

export default function RegisterPage() {
  const { t } = useTranslation();
  const { design } = useDesign();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [searchParams] = useSearchParams();
  const { register } = useAuth();

  const pc = design.primary_color || '#FF5C00';
  const LogoIcon = LOGO_ICONS[design.logo_icon] || Dumbbell;
  const appLeft = design.app_name_left || 'trainedby';
  const appRight = design.app_name_right || 'RS';
  const navigate = useNavigate();

  const inviteCode = searchParams.get('invite');
  const submittingRef = useRef(false);

  const strength = useMemo(() => getPasswordStrength(password), [password]);

  const checks = useMemo(() => ({
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[^a-zA-Z0-9]/.test(password),
  }), [password]);

  useEffect(() => {
    if (inviteCode) {
      validateInviteCode();
    }
  }, [inviteCode]);

  const validateInviteCode = async () => {
    try {
      const response = await client.get(`/invite-codes/validate/${inviteCode}`);
      setInviteInfo(response.data);
    } catch (error) {
      toast.error(t('register.invalidInviteCode', 'Invalid invite code'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guard synchronously: the disabled={loading} attribute only takes effect
    // on the next render, so a fast double-tap could otherwise fire two
    // /auth/register calls.
    if (submittingRef.current) return;
    if (password.length < 8) {
      toast.error(t('completeSignup.passwordTooShort'));
      return;
    }
    submittingRef.current = true;
    setLoading(true);
    try {
      await register(email, password, name, inviteCode);
      toast.success(t('completeSignup.accountCreated'));
      const redirectTo = searchParams.get('redirect');
      navigate(redirectTo?.startsWith('/routine/') ? redirectTo : '/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      const msg = error.response?.data?.detail
        || error.response?.data?.error
        || error.response?.data?.message
        || (error.response?.status === 429 ? t('auth.tooManyAttempts', 'Too many attempts. Please wait a moment and try again.') : t('auth.registrationFailed', 'Registration failed. Please try again.'));
      toast.error(msg);
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const handleOAuthRegister = async (provider) => {
    try {
      const redirectUri = `${window.location.origin}/auth/callback/${provider}`;
      const response = await client.get(`/auth/oauth/url?provider=${provider}&redirect_uri=${encodeURIComponent(redirectUri)}&intent=register`);
      window.location.href = response.data.url;
    } catch (err) {
      toast.error(t('auth.oauthFailed', 'Failed to start registration'));
    }
  };

  const CheckItem = ({ met, label }) => (
    <div className={`flex items-center gap-1.5 text-xs transition-colors ${met ? 'text-green-500' : 'text-muted-foreground'}`}>
      {met ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      <span>{label}</span>
    </div>
  );

  // Registration is invite-only — show closed screen if no invite code
  if (!inviteCode) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="register-closed-page">
        <div className="w-full max-w-md space-y-8 text-center">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3">
            <div className="p-3 rounded-xl" style={{ backgroundColor: pc, boxShadow: `0 0 20px ${pc}26` }}>
              <LogoIcon className="h-8 w-8 text-white" />
            </div>
            <span className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {appLeft}<span style={{ color: pc }}>{appRight}</span>
            </span>
          </div>

          {/* Closed message */}
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="p-5 rounded-full bg-muted">
                <Lock className="h-10 w-10 text-muted-foreground" />
              </div>
            </div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('register.closedTitle')}
            </h1>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t('register.closedDesc')}<br />
              {t('register.closedReach')}
            </p>
          </div>

          {/* Social links */}
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{t('register.getInTouch')}</p>
            <div className="flex flex-col gap-3">
              <a
                href="https://www.instagram.com/ruisantos.125/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-xl border border-border/60 hover:border-[#E1306C]/50 hover:bg-[#E1306C]/5 transition-all font-medium text-sm"
              >
                <svg className="h-5 w-5 text-[#E1306C]" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                {t('register.instagram')}
              </a>
              <a
                href="https://wa.me/351961692558"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-xl border border-border/60 hover:border-[#25D366]/50 hover:bg-[#25D366]/5 transition-all font-medium text-sm"
              >
                <svg className="h-5 w-5 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                {t('register.whatsapp')}
              </a>
              <a
                href="https://www.facebook.com/iRSantoz/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-xl border border-border/60 hover:border-[#1877F2]/50 hover:bg-[#1877F2]/5 transition-all font-medium text-sm"
              >
                <svg className="h-5 w-5 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                {t('register.facebook')}
              </a>
            </div>
          </div>

          <div className="text-sm text-center">
            <span className="text-muted-foreground">{t('register.alreadyHaveAccount')} </span>
            <Link to="/login" className="font-medium hover:underline" style={{ color: pc }}>{t('register.signIn')}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left side - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1543352632-5a4b24e4d2a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwbWVhbCUyMHByZXAlMjBjb250YWluZXIlMjBmb29kfGVufDB8fHx8MTc3MDgwNDUyMHww&ixlib=rb-4.1.0&q=85"
          alt="Healthy meal prep"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h1 className="text-5xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            FUEL YOUR<br />
            <span className="text-primary">PERFORMANCE.</span>
          </h1>
          <p className="text-neutral-300 text-lg">
            {t('auth.heroSubtitle', 'Join thousands of athletes and coaches transforming their fitness journey.')}
          </p>
        </div>
      </div>

      {/* Right side - Register Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="p-3 rounded-xl" style={{ backgroundColor: pc, boxShadow: `0 0 20px ${pc}26` }}>
                <LogoIcon className="h-8 w-8 text-white" />
              </div>
              <span className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {appLeft}<span style={{ color: pc }}>{appRight}</span>
              </span>
            </div>
          </div>

          {/* Routine Context Banner */}
          {!inviteInfo && searchParams.get('redirect')?.startsWith('/routine/') && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
              <BookmarkPlus className="h-4 w-4 text-primary shrink-0" />
              {t('register.routineBanner')}
            </div>
          )}

          {/* Invite Code Banner */}
          {inviteInfo && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/10 border border-primary/30">
              <UserCheck className="h-6 w-6 text-primary" />
              <div>
                <p className="font-medium text-primary">{t('register.invitedBy', { name: inviteInfo.coach_name })}</p>
                <p className="text-sm text-muted-foreground">
                  {t('register.autoAssigned')}
                </p>
              </div>
            </div>
          )}

          <LanguagePickerLarge className="mb-4" />

          <Card className="border-border/50 shadow-xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold tracking-tight" style={{ fontFamily: `'Barlow Condensed', sans-serif` }}>
                {t('auth.createAccount').toUpperCase()}
              </CardTitle>
              <CardDescription>
                {t('auth.registerSubtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">{t('auth.fullName')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">{t('auth.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  {/* Password Strength Meter */}
                  {password.length > 0 && (
                    <div className="space-y-2 pt-1" data-testid="password-strength-meter">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 flex gap-1">
                          {[1, 2, 3, 4, 5].map((level) => (
                            <div
                              key={level}
                              className="h-1.5 flex-1 rounded-full transition-all duration-300"
                              style={{
                                backgroundColor: level <= strength.score ? strength.color : 'hsl(var(--muted))',
                              }}
                            />
                          ))}
                        </div>
                        <span className="text-xs font-medium min-w-[70px] text-right" style={{ color: strength.color }}>
                          {strength.labelKey ? t(strength.labelKey) : ''}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <CheckItem met={checks.length} label={t('register.passwordLength')} />
                        <CheckItem met={checks.upper} label={t('register.passwordUppercase')} />
                        <CheckItem met={checks.number} label={t('register.passwordNumber')} />
                        <CheckItem met={checks.special} label={t('register.passwordSpecial')} />
                      </div>
                    </div>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(255,92,0,0.3)] hover:shadow-[0_0_25px_rgba(255,92,0,0.5)]"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.saving')}
                    </>
                  ) : (
                    t('auth.registerButton')
                  )}
                </Button>
              </form>

              {/* OAuth Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">
                    {t('auth.orContinueWith', 'or continue with')}
                  </span>
                </div>
              </div>

              {/* OAuth Buttons */}
              <div className="space-y-2.5">
                <button
                  type="button"
                  onClick={() => handleOAuthRegister('facebook')}
                  className="w-full flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-md text-white font-medium text-sm transition-opacity hover:opacity-90"
                  style={{ backgroundColor: '#1877F2' }}
                  data-testid="register-facebook-btn"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  {t('auth.continueWithFacebook', 'Continue with Facebook')}
                </button>
                <button
                  type="button"
                  onClick={() => handleOAuthRegister('instagram')}
                  className="w-full flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-md text-white font-medium text-sm transition-opacity hover:opacity-90"
                  style={{ background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
                  data-testid="register-instagram-btn"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C16.67.014 16.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                  {t('auth.continueWithInstagram', 'Continue with Instagram')}
                </button>
              </div>

              <div className="mt-6 text-center text-sm">
                <span className="text-muted-foreground">{t('auth.hasAccount')} </span>
                <Link to="/login" className="text-primary hover:text-primary/90 font-medium" data-testid="login-link">
                  {t('auth.signIn')}
                </Link>
              </div>
              <div className="mt-3 flex items-center justify-center gap-3 text-xs text-muted-foreground">
                <Link to="/privacy-policy" className="hover:underline" data-testid="register-privacy-link">
                  {t('register.privacyPolicy')}
                </Link>
                <span>|</span>
                <Link to="/terms-of-service" className="hover:underline" data-testid="register-terms-link">
                  {t('register.termsOfService')}
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
